import last from 'lodash/last';
import dynamic from 'next/dynamic';
import { Post } from 'lib/types';
import { getYoutubeVideoId, isYouTubeEmbedDisabled } from 'lib/helpers';
import { NETWORKS } from 'lib/constants';
import { LoadingAnimation } from 'components/LoadingAnimation';
import { imagePostUrl } from 'lib/utils/imageUtils';
import { EmbedFallback } from './EmbedFallback';

const renderLoading = () => <LoadingAnimation loading textArray={['Loading post...']} />;

const Tweet = dynamic(
  () => import('react-twitter-widgets').then((mod) => mod.Tweet),
  { ssr: false, loading: renderLoading },
);

const YouTube = dynamic(
  () => import('react-youtube'),
  { ssr: false, loading: renderLoading },
);

const TikTokEmbed = dynamic(
  () => import('components/TikTokEmbed/TikTokEmbed'),
  { ssr: false, loading: renderLoading },
);

const InstagramEmbedWithFallback = dynamic(
  () => import('./InstagramEmbedWithFallback').then((mod) => mod.InstagramEmbedWithFallback),
  { ssr: false, loading: renderLoading },
);

const TwitchEmbed = dynamic(
  () => import('./TwitchEmbed').then((mod) => mod.TwitchEmbed),
  { ssr: false, loading: renderLoading },
);

const ThreadsEmbed = dynamic(
  () => import('./ThreadsEmbed').then((mod) => mod.ThreadsEmbed),
  { ssr: false, loading: renderLoading },
);

const LinkedinEmbed = dynamic(
  () => import('./LinkedinEmbed').then((mod) => mod.LinkedinEmbed),
  { ssr: false, loading: renderLoading },
);

interface Props {
  post: Post;
}

export const PostEmbed = ({ post }: Props) => {
  if (!post.id) {
    return <LoadingAnimation loading textArray={['Loading post...']} />;
  }

  const networkName = (NETWORKS[post.network] || {}).name;

  if (!post.isLive) {
    return <EmbedFallback post={post} />;
  }

  if (networkName === 'twitter') {
    const tweetId = last(post.url.split('/status/')) as string;
    return (
      <Tweet
        tweetId={tweetId}
      />
    );
  } if (networkName === 'instagram') {
    return <InstagramEmbedWithFallback post={post} />;
  }

  if (networkName === 'facebook') {
    return <EmbedFallback post={post} />;
  }

  if (networkName === 'youtube') {
    if (isYouTubeEmbedDisabled(post.account)) {
      return <EmbedFallback post={post} />;
    }
    return (
      <YouTube
        videoId={getYoutubeVideoId(post.url)}
        containerClassName="youtubeContainer"
        opts={{ width: '100%' }}
      />
    );
  }

  if (networkName === 'tiktok') {
    return (
      <TikTokEmbed post={post} />
    );
  }

  if (networkName === 'twitch') {
    return (
      <TwitchEmbed post={post} />
    );
  }

  if (networkName === 'threads') {
    return (
      <ThreadsEmbed post={post} />
    );
  }

  if (networkName === 'linkedin') {
    return (
      <LinkedinEmbed post={post} />
    );
  }

  if (networkName === 'snapchat') {
    return (
      <EmbedFallback post={post} />
    );
  }

  const backgroundImageStyle = {
    backgroundImage: post.thumbnailUrl ? `url(${imagePostUrl(post.thumbnailUrl, { isDead: !post.isLive })})` : 'none',
  };
  return (
    <div className="bg-contain bg-no-repeat h-full" style={backgroundImageStyle} />
  );
};
